import { Fragment } from 'react';

import { IconRadioChecked } from '@components/icons/radio-checked';
import { IconRadioIdle } from '@components/icons/radio-idle';
import { NumberField } from '@components/number-field';

import type { MenuProps } from './types';

import { FilterActionButtons } from '../actions-buttons';
import { useFilterTranslate } from '../filter-utils';
import { FilterListItem } from '../list-item';
import { FilterComparer } from '../shared-types';

const DEFAULT_NUMBER_COMPARERS = ['is', 'not_is', 'lt', 'gt'] satisfies Array<FilterComparer>;

type NumberComparer = (typeof DEFAULT_NUMBER_COMPARERS)[number];

export const isValidNumberComparer = (comparer: FilterComparer): comparer is NumberComparer => {
  return DEFAULT_NUMBER_COMPARERS.includes(comparer as NumberComparer);
};

export const DecimalMenu = ({ state, actions, config }: MenuProps) => {
  return <NumberMenu state={state} actions={actions} config={config} isDecimal />;
};

export const NumberMenu = ({
  state,
  actions,
  config,
  isDecimal = false,
}: MenuProps & { isDecimal?: boolean }) => {
  const { t } = useFilterTranslate();
  const comparers =
    ('allowedComparers' in config && config.allowedComparers) || DEFAULT_NUMBER_COMPARERS;

  return (
    <Fragment>
      {comparers.map((comparer) => {
        const isCurrentComparer = state.comparer === comparer;
        const label = t(`core.comparator.${comparer}`) || comparer;

        return (
          <Fragment key={comparer}>
            <FilterListItem
              icon={isCurrentComparer ? <IconRadioChecked /> : <IconRadioIdle />}
              label={label.replace('...', '')}
              onClick={() => {
                actions.selectComparer(comparer);
                if (state.value) {
                  actions.selectValue(state.value);
                }
              }}
            />
            {isCurrentComparer && (
              <NumberField
                autoFocus
                onChange={(value) => actions.selectValue(String(value))}
                defaultValue={(state.value as string) || undefined}
                label={label}
                hideLabel
                isDecimal={isDecimal}
              />
            )}
          </Fragment>
        );
      })}
      <FilterActionButtons state={state} actions={actions} disableApply={!state.value} />
    </Fragment>
  );
};
